/**
 * Credit card data-access layer for the Add/Edit Payment Account modals.
 *
 * The real flow is two-step (verify→create): raw PAN/CVV go ONLY to the verify step; create
 * receives a gateway token + masked data.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  AddCreditCardDocument,
  type AddCreditCardFailureCode,
  type AddCreditCardInput,
  type CardNetwork,
  EditCreditCardDocument,
  type EditCreditCardFailureCode,
  type EditCreditCardInput,
  RemoveCreditCardDocument,
  type RemoveCreditCardFailureCode,
  type VerifyCreditCardFailureCode,
  VerifyCreditCardInformationDocument,
} from '@/graphql/generated/graphql';

import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';

/** Gateway verify result — matches the live `verifyCreditCardInformation` success type. */
export interface VerifyResult {
  gatewayTokenId: string;
  authTransactionId: string;
  network: CardNetwork;
}

/** Thrown when verifyCreditCardInformation returns a domain Failure variant (DECLINED | UNSUPPORTED_NETWORK). */
export class PaymentVerifyError extends Error {
  constructor(public readonly code: VerifyCreditCardFailureCode) {
    super(code);
    this.name = 'PaymentVerifyError';
  }
}

/**
 * Thrown when addCreditCard returns its Failure variant. No real failure codes exist yet —
 * `AddCreditCardFailureCode` has a single `RESERVED` placeholder (the mutation has no domain-decline
 * path today) — but the union still has to be handled since TypeScript forces the branch.
 */
export class PaymentAddCardError extends Error {
  constructor(public readonly code: AddCreditCardFailureCode) {
    super(code);
    this.name = 'PaymentAddCardError';
  }
}

/**
 * Thrown when editCreditCard returns its Failure variant. `EditCreditCardFailureCode` has a
 * single `NOT_FOUND` value, deliberately overloading unknown id / not-owned-by-caller / deactivated
 * card to avoid an enumeration oracle (see backend `CreditCardService.edit()`).
 */
export class PaymentEditCardError extends Error {
  constructor(public readonly code: EditCreditCardFailureCode) {
    super(code);
    this.name = 'PaymentEditCardError';
  }
}

/**
 * Thrown when removeCreditCard returns its Failure variant (NOT_FOUND, deliberately overloading
 * unknown id / not-owned-by-caller / already-inactive to avoid an enumeration oracle — same
 * reasoning as PaymentEditCardError — or DEFAULT_CARD when the target is the caller's default).
 */
export class PaymentRemoveCardError extends Error {
  constructor(public readonly code: RemoveCreditCardFailureCode) {
    super(code);
    this.name = 'PaymentRemoveCardError';
  }
}

export interface VerifyCardRequest {
  cardNumber: string;
  cvv: string;
  expirationMonth: number;
  expirationYear: number;
}

export interface CreateProfileResult {
  id: string;
}

export async function requestVerifyCard(input: VerifyCardRequest): Promise<VerifyResult> {
  const data = await gqlRequest(VerifyCreditCardInformationDocument, {
    input: {
      CardNumber: input.cardNumber,
      CVV: input.cvv,
      ExpirationMonth: input.expirationMonth,
      ExpirationYear: input.expirationYear,
    },
  });
  const result = data.verifyCreditCardInformation;
  if (result.__typename === 'VerifyCreditCardInformationFailure') {
    throw new PaymentVerifyError(result.code);
  }
  return {
    gatewayTokenId: result.gatewayTokenId,
    authTransactionId: result.authTransactionId,
    network: result.network,
  };
}

export async function requestCreateCardProfile(input: AddCreditCardInput): Promise<CreateProfileResult> {
  const data = await gqlRequest(AddCreditCardDocument, { input });
  const result = data.addCreditCard;
  if (result.__typename === 'AddCreditCardFailure') {
    throw new PaymentAddCardError(result.code);
  }
  return { id: result.card.id };
}

/**
 * Edits a saved credit card in place. Returns the updated card as reported by the mutation — the
 * Salesforce Flow atomically demotes any prior default server-side, but the response only carries
 * this one card, so the caller is responsible for reconciling other rows (see useEditCreditCard).
 */
export async function requestEditCreditCard(id: string, input: EditCreditCardInput): Promise<CreditCardAccount> {
  const data = await gqlRequest(EditCreditCardDocument, { id, input });
  const result = data.editCreditCard;
  if (result.__typename === 'EditCreditCardFailure') {
    throw new PaymentEditCardError(result.code);
  }
  return mapSavedCreditCard(result.card);
}

/** Removes (soft-deletes) a saved credit card. Rejects the caller's current default — see PaymentRemoveCardError. */
export async function requestRemoveCreditCard(id: string): Promise<{ id: string }> {
  const data = await gqlRequest(RemoveCreditCardDocument, { input: { id } });
  const result = data.removeCreditCard;
  if (result.__typename === 'RemoveCreditCardFailure') {
    throw new PaymentRemoveCardError(result.code);
  }
  return { id: result.card.id };
}

/**
 * Structural shape mapSavedCreditCard actually reads — loose enough that both the list query's
 * edge and editCreditCard's returned card satisfy it, even though those are nominally distinct
 * generated types (different operations selecting the same fields).
 */
export interface SavedCreditCardFields {
  id: string;
  network: CardNetwork;
  last4: string;
  expirationMonth: number;
  expirationYear: number;
  nameOnCard?: string | null;
  billingAddress?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  isDefault: boolean;
  isExpired: boolean;
  isHSA: boolean;
  isActive: boolean;
}

export function mapSavedCreditCard(card: SavedCreditCardFields): CreditCardAccount {
  return {
    id: card.id,
    network: card.network,
    last4: card.last4,
    expirationMonth: card.expirationMonth,
    expirationYear: card.expirationYear,
    holderName: card.nameOnCard ?? '',
    line1: card.billingAddress?.line1 ?? '',
    line2: card.billingAddress?.line2 ?? '',
    city: card.billingAddress?.city ?? '',
    state: card.billingAddress?.state ?? '',
    zip: card.billingAddress?.zip ?? '',
    isDefault: card.isDefault,
    isExpired: card.isExpired,
    isHsa: card.isHSA,
    isActive: card.isActive,
  };
}
